import { useState } from 'react';
import { Link } from 'react-router';
import { OS } from 'types';

import NotificationBell from '~/assets/icons/NotificationBell';
import ProfileIcon from '~/assets/icons/Profile';
import SearchIcon from '~/assets/icons/Search';
import SoleIcon from '~/assets/icons/Sole';
import StandardModal from '~/components/StandardModal';
import Tracked from '~/components/Tracked';
import useIsSignedIn from '~/hooks/useIsSignedIn';
import useRootData from '~/hooks/useRootData';
import useWindowSize from '~/hooks/useWindowSize';
import {
  getIsLargeBreakpoint,
  getIsMediumBreakpoint
} from '~/utils/breakpoints';
import { isSSR } from '~/utils/dom';

import MegaNav from './MegaNav';
import megaNavMenu from './MegaNav/menu';
import MobileMenu from './MobileMenu';
import mobileMenu from './MobileMenu/menu';
import ProfileSignInButton from './ProfileSignInButton';
import SearchPanel from './SearchPanel';

export default function MainHeader() {
  const { isMobileRequest, clientOs } = useRootData();
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const [searchPanel, setSearchPanel] = useState(false);
  const isSignedIn = useIsSignedIn();
  const windowObject = !isSSR() ? window : {};
  const windowSize = useWindowSize(windowObject);
  const { width: windowWidth } = windowSize || {};

  const isAndroidClient = clientOs === OS.Android;
  const isLargeBreakpoint = getIsLargeBreakpoint(windowWidth, isMobileRequest);
  const isMediumBreakpoint = getIsMediumBreakpoint(
    windowWidth,
    isMobileRequest
  );

  const profileIconColor = '#56BD6F';
  const profilePage = '/profile/';

  return (
    <header
      className="sticky top-0 z-[49] flex h-[50px] w-full flex-row justify-center bg-black lg:h-[70px]"
      data-site-header
      role="banner"
    >
      <div className="content-width flex w-full justify-center">
        <section className="flex w-full max-w-desktop flex-row items-center gap-8">
          <Link
            to="/"
            className="w-[50px] max-lg:absolute max-lg:left-1/2 max-lg:ml-[-25px] max-lg:mr-8"
          >
            <SoleIcon width="50" height="50" colour="#FFFFFF" />
          </Link>

          <div className="lg:hidden">
            <MobileMenu
              menu={mobileMenu}
              mobileMenuIsOpen={mobileMenuIsOpen}
              setMobileMenuIsOpen={setMobileMenuIsOpen}
            />
          </div>
          <div className="hidden lg:block">
            <MegaNav menu={megaNavMenu} />
          </div>
        </section>

        <section className="flex items-center gap-5">
          <div className="ml-auto flex w-auto flex-row items-center justify-center lg:w-[200px] min-[1200px]:w-[270px]">
            <div className="size-6 lg:hidden">
              <button
                type="button"
                aria-label="Search"
                className="tap-target"
                onClick={() => {
                  setSearchPanel(true);
                }}
              >
                <SearchIcon width={24} height={24} stroke="#a9a9a9" />
              </button>
              <span className="sr-only">Search</span>
              {searchPanel && !isLargeBreakpoint && (
                <StandardModal
                  backdrop="clear"
                  isFullScreen
                  isFullBody
                  background="concrete"
                  onCloseClick={() => {}}
                  preventBodyScrollLock={false}
                  disableClickOutside
                  hideClose
                >
                  <SearchPanel
                    searchPanelState={[searchPanel, setSearchPanel]}
                    isLargeBreakpoint={!!isLargeBreakpoint}
                    isMediumBreakpoint={!!isMediumBreakpoint}
                  />
                </StandardModal>
              )}
            </div>

            <div className="hidden lg:flex">
              <SearchPanel
                searchPanelState={[searchPanel, setSearchPanel]}
                isLargeBreakpoint={!!isLargeBreakpoint}
                isMediumBreakpoint={!!isMediumBreakpoint}
                defaultToLargeLayout
              />
            </div>
          </div>

          {!isAndroidClient && (
            <Tracked.InternalLink
              name="NOTIFICATION_CENTRE"
              variant="none"
              pagePosition="Header"
              to={'/notification-centre/'}
              className="tap-target cursor-pointer rounded-lg"
            >
              <NotificationBell />
            </Tracked.InternalLink>
          )}

          {isSignedIn ? (
            <Tracked.ExternalLink
              pagePosition="Header"
              name="Sign in"
              variant="none"
              href={profilePage}
              className="tap-target"
            >
              <ProfileIcon color={profileIconColor} />
            </Tracked.ExternalLink>
          ) : (
            <ProfileSignInButton />
          )}
        </section>
      </div>
    </header>
  );
}
